import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Download, Search, ZoomIn, ZoomOut, X, Printer, Loader2, Sidebar as SidebarIcon, ChevronDown, ChevronUp } from 'lucide-react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Initialize PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PdfViewer = ({ document: docObj, onClose }) => {
    const [numPages, setNumPages] = useState(null);
    const [scale, setScale] = useState(1.0);
    const [searchText, setSearchText] = useState('');
    const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
    const [totalMatches, setTotalMatches] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [sidebarWidth, setSidebarWidth] = useState(224);
    const [isResizing, setIsResizing] = useState(false);
    const containerRef = useRef(null);
    const pageRefs = useRef({});

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
        setIsLoading(false);
    };

    // Setup observer to count dynamic DOM matches
    useEffect(() => {
        if (!containerRef.current) return;
        const updateMatches = () => {
            const marks = containerRef.current.querySelectorAll('mark.pdf-match');
            if (marks.length !== totalMatches && searchText) {
                setTotalMatches(marks.length);
            }
            if (!searchText && totalMatches !== 0) {
                setTotalMatches(0);
            }
            marks.forEach((mark, index) => {
                const targetClass = index === currentMatchIndex
                    ? 'pdf-match bg-orange-500 text-white rounded px-1 shadow-sm'
                    : 'pdf-match bg-yellow-300 text-yellow-900 rounded px-0.5';
                if (mark.className !== targetClass) {
                    mark.className = targetClass;
                }
            });
        };

        const observer = new MutationObserver(updateMatches);
        observer.observe(containerRef.current, { childList: true, subtree: true, characterData: true, attributes: true });
        updateMatches();
        return () => observer.disconnect();
    }, [searchText, currentMatchIndex, totalMatches]);

    // Jump to current match naturally
    const jumpToCurrentMatch = useCallback(() => {
        if (!containerRef.current) return;
        const marks = containerRef.current.querySelectorAll('mark.pdf-match');
        if (marks[currentMatchIndex]) {
            marks[currentMatchIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [currentMatchIndex]);

    useEffect(() => {
        jumpToCurrentMatch();
    }, [currentMatchIndex, jumpToCurrentMatch]);

    // Sidebar Resizing Logic
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isResizing) return;
            const newWidth = Math.min(Math.max(e.clientX, 150), 600);
            setSidebarWidth(newWidth);
        };
        const handleMouseUp = () => setIsResizing(false);

        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing]);

    const handleSearch = (e) => {
        setSearchText(e.target.value);
        setCurrentMatchIndex(0); // Reset to first match
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (totalMatches > 0) {
                // Keep moving to next
                setCurrentMatchIndex((prev) => (prev + 1) % totalMatches);
            }
        }
    };

    const nextMatch = () => {
        if (totalMatches > 0) {
            setCurrentMatchIndex((prev) => (prev + 1) % totalMatches);
        }
    };

    const prevMatch = () => {
        if (totalMatches > 0) {
            setCurrentMatchIndex((prev) => (prev - 1 + totalMatches) % totalMatches);
        }
    };

    const textRenderer = useCallback((textItem) => {
        if (!searchText) return textItem.str;
        const regex = new RegExp(`(${searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        return textItem.str.replace(regex, '<mark class="pdf-match bg-yellow-300 text-yellow-900 rounded px-0.5">$1</mark>');
    }, [searchText]);

    const scrollToPage = (pageIndex) => {
        if (pageRefs.current[pageIndex]) {
            pageRefs.current[pageIndex].scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const zoomIn = () => setScale(s => Math.min(s + 0.2, 3.0));
    const zoomOut = () => setScale(s => Math.max(s - 0.2, 0.5));

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = docObj.content;
        link.download = docObj.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handlePrint = () => {
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = docObj.content;
        document.body.appendChild(iframe);
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
    };

    return (
        <div className="flex flex-col h-full bg-slate-50" onClick={e => e.stopPropagation()}>
            {/* Toolbar */}
            <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-slate-200 shrink-0 shadow-sm">

                <div className="flex items-center gap-1 sm:gap-4 flex-1">
                    <button onClick={() => setSidebarOpen(!sidebarOpen)} className={`p-2 rounded-lg transition-colors ${sidebarOpen ? 'bg-forest-100 text-forest-700' : 'text-slate-500 hover:bg-slate-100'}`} title="Toggle Sidebar">
                        <SidebarIcon className="w-5 h-5" />
                    </button>
                    <div>
                        <h3 className="text-slate-900 font-semibold text-sm truncate max-w-xs">{docObj.name}</h3>
                        <p className="text-slate-500 text-xs">{docObj.size}</p>
                    </div>

                    {/* Search Component */}
                    <div className="relative flex items-center bg-slate-100 rounded-lg overflow-hidden border border-slate-200 ml-4 focus-within:border-forest-500 focus-within:ring-1 focus-within:ring-forest-500 transition-all">
                        <div className="pl-3 pr-2 py-1.5 flex items-center justify-center pointer-events-none">
                            <Search className="w-4 h-4 text-slate-400" />
                        </div>
                        <input
                            type="text"
                            placeholder="Find... (Press Enter to cycle)"
                            value={searchText}
                            onChange={handleSearch}
                            onKeyDown={handleKeyDown}
                            className="bg-transparent border-none outline-none text-sm w-48 sm:w-64 py-1.5 placeholder:text-slate-400"
                        />
                        {searchText && (
                            <div className="flex items-center bg-slate-200 border-l border-slate-300">
                                <span className="text-xs text-slate-600 px-3 font-medium min-w-[3rem] text-center">
                                    {totalMatches > 0 ? `${currentMatchIndex + 1}/${totalMatches}` : '0/0'}
                                </span>
                                <div className="flex flex-col border-l border-slate-300 h-full">
                                    <button onClick={prevMatch} className="p-1 hover:bg-slate-300 text-slate-600 border-b border-slate-300 transition-colors"><ChevronUp className="w-3 h-3" /></button>
                                    <button onClick={nextMatch} className="p-1 hover:bg-slate-300 text-slate-600 transition-colors"><ChevronDown className="w-3 h-3" /></button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-1 sm:gap-2">

                    {/* Zoom */}
                    <div className="flex items-center bg-slate-100 rounded-lg p-0.5 border border-slate-200 mr-2 hidden sm:flex">
                        <button onClick={zoomOut} className="p-1.5 rounded hover:bg-white text-slate-600 transition-colors"><ZoomOut className="w-4 h-4" /></button>
                        <span className="text-xs font-medium text-slate-600 px-2 w-12 text-center">{Math.round(scale * 100)}%</span>
                        <button onClick={zoomIn} className="p-1.5 rounded hover:bg-white text-slate-600 transition-colors"><ZoomIn className="w-4 h-4" /></button>
                    </div>

                    {/* Actions */}
                    <button onClick={handlePrint} className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors hidden sm:block" title="Print"><Printer className="w-4 h-4" /></button>
                    <button onClick={handleDownload} className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors" title="Download"><Download className="w-5 h-5" /></button>
                    <div className="w-px h-6 bg-slate-200 mx-1"></div>
                    <button onClick={onClose} className="p-2 text-slate-500 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors" title="Close"><X className="w-5 h-5" /></button>
                </div>

            </div>

            {/* Main Layout Area */}
            <div className="flex-1 overflow-hidden flex relative">

                {/* Collapsible Sidebar */}
                {sidebarOpen && numPages && (
                    <div className="flex shrink-0 relative bg-white border-r border-slate-200 z-10" style={{ width: sidebarWidth }}>
                        <div className="flex flex-col overflow-y-auto w-full h-full p-4 space-y-4">
                            <Document file={docObj.content}>
                                {Array.from(new Array(numPages), (el, index) => (
                                    <div key={`thumb_${index + 1}`} onClick={() => scrollToPage(index + 1)} className="cursor-pointer group">
                                        <div className="w-full bg-slate-50 border border-slate-200 group-hover:border-forest-400 group-hover:ring-2 group-hover:ring-forest-100 rounded-lg overflow-hidden transition-all flex items-center justify-center">
                                            <Page
                                                pageNumber={index + 1}
                                                width={Math.max(100, sidebarWidth - 40)}
                                                renderAnnotationLayer={false}
                                                renderTextLayer={false}
                                            />
                                        </div>
                                        <p className="text-center text-xs text-slate-500 mt-2 font-medium">Page {index + 1}</p>
                                    </div>
                                ))}
                            </Document>
                        </div>
                        {/* Resizer Handle */}
                        <div
                            onMouseDown={() => setIsResizing(true)}
                            className="absolute right-0 top-0 bottom-0 w-2 hover:bg-forest-400/50 cursor-col-resize flex justify-center items-center transition-colors group z-20"
                            style={{ transform: 'translateX(50%)' }}
                        >
                            <div className="w-0.5 h-12 bg-slate-300 group-hover:bg-forest-600 rounded-full" />
                        </div>
                    </div>
                )}

                {/* Document Render Area (Continuous Scrolling) */}
                <div className="flex-1 overflow-auto bg-slate-200 w-full relative flex flex-col items-center py-6" ref={containerRef}>
                    {isLoading && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 bg-slate-50 z-10">
                            <Loader2 className="w-8 h-8 animate-spin mb-4" />
                            <p className="text-sm font-medium">Loading Document Viewer...</p>
                        </div>
                    )}

                    <Document
                        file={docObj.content}
                        onLoadSuccess={onDocumentLoadSuccess}
                        className="flex flex-col items-center w-full"
                        loading={null}
                        error={<div className="p-6 text-red-500 font-medium bg-white rounded-lg shadow-sm border border-red-100">Failed to load PDF file.</div>}
                    >
                        {numPages && Array.from(new Array(numPages), (el, index) => (
                            <div
                                key={`page_${index + 1}`}
                                ref={el => pageRefs.current[index + 1] = el}
                                className="bg-white shadow-xl isolate mb-8 rounded w-auto flex justify-center"
                            >
                                <Page
                                    pageNumber={index + 1}
                                    scale={scale}
                                    renderAnnotationLayer={false}
                                    renderTextLayer={true}
                                    customTextRenderer={textRenderer}
                                    loading={<div className="w-[600px] h-[800px] bg-slate-50 animate-pulse flex items-center justify-center text-slate-400">Loading page {index + 1}...</div>}
                                />
                            </div>
                        ))}
                    </Document>
                </div>
            </div>
        </div>
    );
};

export default PdfViewer;
